import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { skipToken } from "@reduxjs/toolkit/query";
import { chain, isEqual, omit, pick, uniq } from "lodash";
import { createSelectorCreator, defaultMemoize } from "reselect";

import { loadProject } from "fond/project/redux";
import {
  AppThunkDispatch,
  Architecture,
  Configuration,
  ConfigurationResponse,
  ConfigurationUpsert,
  CoreConfigurationRequest,
  CTSBinRangesResponse,
  GroupConfig,
  GroupConfigHydrated,
  ImportStatus,
  ProjectFiles,
  ProjectStatus,
  QuotaUsageEstimate,
  ScenarioConfiguration,
  SpliceTableNode,
  Store,
  Style,
  Task,
  Version,
  VersionCoreConfiguration,
  VersionImportStatus,
  VersionLegacyExport,
  VersionScenarioConfiguration,
  VersionScreenshotResponse,
  VersionV2,
} from "fond/types";
import {
  FilterConfiguration,
  LayerConfig,
  LayerConfigHydrated,
  LayerStyle,
  SublayerConfig,
  SublayerConfigHydrated,
} from "fond/types/ProjectLayerConfig";
import { pickIDs } from "fond/utils";
import { selectGroupsFromConfig, selectLayersFromConfig, selectStylesFromConfig } from "fond/utils/configurations";
import { negativeFilterConfiguration } from "fond/utils/mapbox";

import { apiSlice } from "./apiSlice";
import { orderLayersByConfiguration } from "./draftSlice";

type GetVersionsResponse = {
  Versions: VersionV2[];
};

type GetVersionSpliceTableResponse = {
  SpliceTables: SpliceTableNode[];
};

/*
 * Payload required to update an existing version architecture.
 *
 * Note that ID is either the ID of a version architecture or a base architecture.
 */
type UpdateVersionArchitecture = Partial<
  Pick<
    Architecture,
    | "Name"
    | "Description"
    | "Demand"
    | "NumberOfTiers"
    | "HasAttemptedSave"
    | "IsConfigured"
    | "IsFlexNap"
    | "BOM"
    | "DefaultPlacement"
    | "Tier1"
    | "Tier2"
    | "Tier3"
    | "ID"
    | "DataPrepConfiguration"
  >
>;

export const versionConfigEntityAdapter = createEntityAdapter<GroupConfig | LayerConfig | SublayerConfig | LayerStyle, string>({
  selectId: (item) => item.ID,
});

const initialConfigState: Configuration = {
  ID: "",
  Key: "",
  SourceID: "",
  MapChildren: [],
  Data: versionConfigEntityAdapter.getInitialState(),
  Type: <const>"MapLayerConfig",
};

export const transformVersionResponse = (version: VersionV2): Version => ({
  ...version,
  Creator: { ...version.Creator, Rank: null },
  LastModifiedBy: { ...version.LastModifiedBy, Rank: null },
  Project: version.Project?.ID ?? null,
  Parent: version.Parent?.ID ?? null,
  MapLayerConfigID: version.RootConfigurationID,
  AddressTypeField: version?.AddressTypeField ?? null,
  ProjectCode: version?.ProjectCode ?? null,
});

export const inverseTransformVersionResponse = (version: Version): VersionV2 => ({
  ...version,
  Creator: omit(version.Creator, "Rank"),
  LastModifiedBy: omit(version.LastModifiedBy, "Rank"),
  Project: { ID: version.Project },
  Parent: version.Parent ? { ID: version.Parent } : null,
  RootConfigurationID: version.MapLayerConfigID,
});

export const transformStyleResponse = (style: Style): LayerStyle => {
  const { ID, GlobalPosition, ConfigurationID, ConfigurationType, Name, Position, MapboxStyle, ...RawStyles } = style;

  return {
    ID,
    Name,
    GlobalPosition: GlobalPosition || 0,
    ConfigurationID,
    ConfigurationType,
    Position,
    MapboxStyle,
    RawStyles,
    Type: <const>"STYLE",
  };
};

/**
 * Flattens the backend nested data structure to allow for easier manipulation.
 */
export const transformGetVersionConfigResponse = (response: ConfigurationResponse): Configuration => {
  const { GroupConfigurations, LayerConfigurations, StyleConfigurations, SublayerConfigurations, MapChildren, ...rest } = response;

  const layerConfigs = LayerConfigurations.map(transformLayerConfigResponse);
  const sublayerConfigs = SublayerConfigurations.map((sublayer) => transformSublayerResponse(sublayer, layerConfigs));

  let data = versionConfigEntityAdapter.setAll(versionConfigEntityAdapter.getInitialState(), [
    ...StyleConfigurations.map(transformStyleResponse),
    ...layerConfigs,
    ...sublayerConfigs,
    ...GroupConfigurations.map(transformGroupConfigResponse),
  ]);

  data = versionConfigEntityAdapter.upsertMany(
    data,
    layerConfigs.reduce<SublayerConfig[]>((value, layer) => {
      const other = buildSublayerFilterConfigurationByParentId(layer.ID, data.entities);
      if (other) value.push(other);
      return value;
    }, [])
  );
  const configuration: Configuration = {
    ...rest,
    Data: data,
    MapChildren: pickIDs(MapChildren),
    Type: "MapLayerConfig",
  };

  return configuration;
};

/**
 * Convert the SublayerConfig to the flat structure by replacing the collection
 * of Styles to a collection of IDs instead.
 */
export const transformSublayerResponse = (
  sublayer: SublayerConfigHydrated,
  entities: Array<LayerConfigHydrated | LayerConfig | GroupConfig | SublayerConfig | LayerStyle | undefined>
): SublayerConfig => {
  const { Styles, ...rest } = sublayer;
  // The Key and GeometryType are copied from the parent LayerConfig
  const parentLayerConfig = entities.find((entity) => entity?.ID === sublayer.ParentID) as LayerConfig;

  return {
    ...rest,
    GeometryType: parentLayerConfig?.GeometryType,
    Key: parentLayerConfig?.Key || "",
    Styles: pickIDs(Styles),
  };
};

export const buildSublayerFilterConfigurationByParentId = (
  id: string,
  entities: Record<string, LayerConfig | GroupConfig | SublayerConfig | LayerStyle | undefined>
): SublayerConfig | null => {
  const parentLayerConfig = entities[id] as LayerConfig;
  const siblings: SublayerConfig[] = [];

  const sublayer: SublayerConfig | null = parentLayerConfig?.Children.reduce<SublayerConfig | null>((value, childId) => {
    const child = entities[childId] as SublayerConfig;
    if (child.FilterConfiguration?.Type === "other") {
      return child;
    } else {
      siblings.push(child);
      return value;
    }
  }, null);

  if (sublayer) {
    return {
      ...sublayer,
      Styles: [...sublayer.Styles],
      FilterConfiguration: {
        ...sublayer.FilterConfiguration,
        Mapbox: negativeFilterConfiguration(siblings),
      } as FilterConfiguration,
    };
  }

  return null;
};

/**
 * Convert the LayerConfig to the flat structure by replacing the collection
 * of Children & Styles to a collection of IDs instead.
 */
export const transformLayerConfigResponse = (layer: LayerConfigHydrated): LayerConfig => {
  const { Children, Styles, ...rest } = layer;

  return {
    ...rest,
    Children: pickIDs(Children),
    Styles: pickIDs(Styles),
  };
};

export const transformGroupConfigResponse = (group: GroupConfigHydrated): GroupConfig => {
  const { Children, ...rest } = group;

  return {
    ...rest,
    Children: pickIDs(Children),
  };
};

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const versionsAdapter = createEntityAdapter<Version>({
  selectId: (entity: Version): string => entity.ID,
});
const versionsInitialState = versionsAdapter.getInitialState();

/**
 * Versions API Slice
 */
export const versionsSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getVersion: build.query<Version, string>({
      query: (versionId: string) => `/v2/versions/${versionId}`,
      transformResponse: transformVersionResponse,
      providesTags: (result) => (result ? [{ type: "Version", id: result.ID }] : []),
    }),
    getVersionSpliceTable: build.query<SpliceTableNode[], string>({
      query: (versionId) => ({
        url: `/v2/versions/${versionId}/splice-tables`,
      }),
      transformResponse: (response: GetVersionSpliceTableResponse) => response.SpliceTables,
      providesTags: (result, error, versionId) => [{ type: "SpliceTable", id: versionId }],
    }),
    getVersionConfig: build.query<Configuration, string>({
      query: (versionId: string) => `/v2/versions/${versionId}/root-configuration`,
      transformResponse: transformGetVersionConfigResponse,
      providesTags: (result, error, versionId) => (result ? [{ type: "VersionRootConfiguration", id: versionId }] : []),
    }),
    getVersionCostBinRanges: build.query<CTSBinRangesResponse, string>({
      query: (versionId: string) => `/v2/versions/${versionId}/cost-bin-ranges`,
    }),
    getVersions: build.query({
      query: (projectId: string) => `/v2/projects/${projectId}/versions`,
      transformResponse: (response: GetVersionsResponse) =>
        versionsAdapter.setAll(versionsInitialState, response.Versions.map(transformVersionResponse)),
      providesTags: (result, error, arg) =>
        result ? [...result.ids.map((id) => ({ type: "Version" as const, id })), { type: "Versions", id: arg }] : [{ type: "Versions", id: "LIST" }],
    }),
    updateVersion: build.mutation<Version, Pick<Version, "ID"> & Partial<Pick<Version, "Name" | "ProjectCode" | "PowerArchitecture">>>({
      query: ({ ID, ...version }) => ({
        url: `/v2/versions/${ID}`,
        method: "PATCH",
        body: version,
      }),
      transformResponse: transformVersionResponse,
      // The pessimistic update below means we don't have to invalidate the getVersion query, but getVersion and getVersions
      // have different response shapes so we still want to invalidate getVersions.
      invalidatesTags: (result) => (result ? [{ type: "Versions", id: result.Project }] : []),
      onQueryStarted: async ({ ID }, { dispatch, queryFulfilled }) => {
        const updatedVersion = await queryFulfilled.then(({ data }) => data).catch(() => null);
        if (!updatedVersion) return;

        dispatch(
          versionsSlice.util.updateQueryData("getVersion", ID, (draft) => {
            Object.assign(draft, updatedVersion);
          })
        );
      },
    }),
    createVersion: build.mutation<Version, { projectId: string; versionId: string; name: string; method?: "copy_design" | null }>({
      query: ({ versionId, name, method }) => ({
        url: "/v2/versions",
        method: "POST",
        body: {
          ParentID: versionId,
          Name: name,
          Method: method ? { Type: method } : null,
        },
      }),
      transformResponse: transformVersionResponse,
      invalidatesTags: (result, error, arg) => [{ type: "Versions", id: arg.projectId }],
    }),
    copyVersion: build.mutation<Task, { versionId: string; name?: string; description?: string }>({
      query: ({ versionId, name, description }) => ({
        url: `/v2/versions/${versionId}/copy`,
        method: "POST",
        body: {
          Name: name ?? null,
          Description: description ?? null,
        },
      }),
    }),
    deleteVersion: build.mutation<void, Pick<Version, "ID" | "Project">>({
      query: ({ ID }) => ({
        url: `/v2/versions/${ID}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Versions", id: arg.Project }],
    }),
    getImportStatus: build.query<VersionImportStatus, { versionId?: string; status?: ImportStatus[] }>({
      query: ({ versionId, status }) => {
        if (Boolean(versionId) === Boolean(status)) {
          throw new Error("Querying import status requires either versionId or list of status.");
        }
        return {
          url: `/v2/import-status`,
          params: { version_id: versionId, status: status?.join(",") },
        };
      },
      providesTags: (result, error, { versionId }) => (versionId ? [{ type: "ImportStatus", id: versionId }] : []),
    }),
    getVersionLegacyExport: build.query<ProjectFiles, string>({
      query: (versionId: string) => `/v2/versions/${versionId}/legacy-export`,
      // VersionLegacyExport has a SpliceTableExists field, but we don't actually care about it.
      transformResponse: (response: VersionLegacyExport) => response.Layers,
    }),
    getVersionStatus: build.query<ProjectStatus, string>({
      query: (versionId: string) => `/v2/versions/${versionId}/status`,
      providesTags: (result, error, versionId) => (result ? [{ type: "VersionStatus", id: versionId }] : []),
    }),
    getVersionScreenshot: build.query<VersionScreenshotResponse, string>({
      query: (versionId: string) => `/v2/versions/${versionId}/screenshot`,
      providesTags: (result, error, versionId) => (result ? [{ type: "VersionScreenshot", id: versionId }] : []),
    }),
    getVersionEstimatedQuotaUsage: build.query<QuotaUsageEstimate, string>({
      query: (versionId: string) => ({
        url: `/v2/versions/${versionId}/workflow-quota-usage-estimate`,
        // This endpoint is a POST request to support custom workflows for design projects; at the moment
        // it only supports planner projects and takes no inputs.
        method: "POST",
      }),
      providesTags: (result, error, versionId) =>
        result
          ? [
              { type: "Layers", id: versionId },
              { type: "Subscription", id: "LIST" },
            ]
          : [],
    }),
    cancelVersionWorkflow: build.mutation<void, string>({
      query: (versionId) => ({
        url: `/v2/versions/${versionId}/workflow`,
        method: "PATCH",
        body: {
          Status: "Cancelled",
        },
      }),
      invalidatesTags: (result, error, versionId) => [{ type: "VersionStatus", id: versionId }],
    }),
    updateVersionArchitecture: build.mutation<Architecture, { versionId: string; architecture: UpdateVersionArchitecture }>({
      query: ({ versionId, architecture }) => ({
        url: `/v2/versions/${versionId}/architecture`,
        method: "PATCH",
        // The internal architecture object is filled with all sorts of fun fields.
        // Ensure we're sending only those fields we know we're allowed to send.
        body: pick(architecture, [
          "Name",
          "Description",
          "Demand",
          "NumberOfTiers",
          "HasAttemptedSave",
          "IsConfigured",
          "IsFlexNap",
          "BOM",
          "DefaultPlacement",
          "Tier1",
          "Tier2",
          "Tier3",
          "ID",
          "DataPrepConfiguration",
        ]),
      }),
      invalidatesTags: (result, error, arg) => [{ type: "VersionStatus", id: arg.versionId }],
      onQueryStarted: async ({ versionId, architecture }, { dispatch, queryFulfilled }) => {
        // Do a pessimistic update of the getVersion response to avoid refetching the version response.
        const { data: updatedArchitecture } = await queryFulfilled;
        dispatch(
          versionsSlice.util.updateQueryData("getVersion", versionId, (draft) => {
            draft.Architecture = updatedArchitecture;
          })
        );
      },
    }),
    reapplyDemandModel: build.mutation<any, { projectId: string; versionId: string }>({
      query: ({ versionId }) => ({
        url: `/v2/versions/${versionId}/reapply-demand-model`,
        method: "PATCH",
      }),
      onQueryStarted: async ({ projectId }, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(loadProject({ uuid: projectId, forceRefetch: true }));
      },
      invalidatesTags: (result, err, { versionId }) => [{ type: "Layers", id: versionId }],
    }),
    getVersionScenarioConfiguration: build.query<VersionScenarioConfiguration, string>({
      query: (versionId) => ({
        url: `/v2/versions/${versionId}/scenario-configuration`,
        method: "GET",
      }),
      providesTags: (result, error, versionId) => [{ type: "VersionScenarioConfiguration", id: versionId }],
    }),
    createVersionScenarioConfiguration: build.mutation<VersionScenarioConfiguration, { versionId: string; configuration: ScenarioConfiguration }>({
      query: ({ versionId, configuration }) => ({
        url: `/v2/versions/${versionId}/scenario-configuration`,
        method: "POST",
        body: configuration,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "VersionScenarioConfiguration", id: arg.versionId }],
    }),
    updateVersionScenarioConfiguration: build.mutation<VersionScenarioConfiguration, { versionId: string; configuration: ScenarioConfiguration }>({
      query: ({ versionId, configuration }) => ({
        url: `/v2/versions/${versionId}/scenario-configuration`,
        method: "PATCH",
        body: configuration,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "VersionScenarioConfiguration", id: arg.versionId }],
    }),
    getVersionCoreConfiguration: build.query<VersionCoreConfiguration, string>({
      query: (versionId) => ({
        url: `/v2/versions/${versionId}/core-configuration`,
        method: "GET",
      }),
      providesTags: (result, error, versionId) => [{ type: "VersionCoreConfiguration", id: versionId }],
    }),
    createVersionCoreConfiguration: build.mutation<VersionCoreConfiguration, { versionId: string; configuration: CoreConfigurationRequest }>({
      query: ({ versionId, configuration }) => ({
        url: `/v2/versions/${versionId}/core-configuration`,
        method: "POST",
        body: configuration,
      }),
      onQueryStarted: async ({ versionId }, { dispatch, queryFulfilled }) => {
        // Do a pessimistic update of the getVersionCoreConfiguration response to avoid refetching it.
        const { data } = await queryFulfilled;
        dispatch(versionsSlice.util.upsertQueryData("getVersionCoreConfiguration", versionId, data));
      },
      invalidatesTags: (result, error, arg) => [{ type: "SpliceTable", id: arg.versionId }],
    }),
    updateVersionCoreConfiguration: build.mutation<VersionCoreConfiguration, { versionId: string; configuration: Partial<CoreConfigurationRequest> }>(
      {
        query: ({ versionId, configuration }) => ({
          url: `/v2/versions/${versionId}/core-configuration`,
          method: "PATCH",
          body: configuration,
        }),
        onQueryStarted: async ({ versionId }, { dispatch, queryFulfilled }) => {
          // Do a pessimistic update of the getVersionCoreConfiguration response to avoid refetching it.
          const { data } = await queryFulfilled;
          dispatch(versionsSlice.util.upsertQueryData("getVersionCoreConfiguration", versionId, data));
        },
        invalidatesTags: (result, error, arg) => [{ type: "SpliceTable", id: arg.versionId }],
      }
    ),
    deleteVersionCoreConfiguration: build.mutation<void, string>({
      query: (versionId) => ({
        url: `/v2/versions/${versionId}/core-configuration`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, versionId) => [
        { type: "VersionCoreConfiguration", id: versionId },
        { type: "SpliceTable", id: versionId },
      ],
    }),
  }),
});

/**
 * Injects into the version config the Cost to Serve information
 */
export const injectIntoVersionConfig = async (dispatch: AppThunkDispatch, versionId: string, upsert: ConfigurationUpsert): Promise<void> => {
  const result = dispatch(versionsSlice.endpoints.getVersionConfig.initiate(versionId));
  const data = (await result)?.data;
  result.unsubscribe();

  if (data) {
    const newConfig: Configuration = {
      ...data,
      MapChildren: uniq([...pickIDs(upsert.filter((entity) => entity.Type === "GROUP")), ...data.MapChildren]),
      Data: versionConfigEntityAdapter.upsertMany(data.Data, upsert),
    };

    await dispatch(versionsSlice.util.upsertQueryData("getVersionConfig", versionId, newConfig));
  }
};

export const useGetVersionConfigQuerySubscription = versionsSlice.endpoints.getVersionConfig.useQuerySubscription;

/**
 * Endpoint Hooks
 */
export const {
  useCopyVersionMutation,
  useCreateVersionMutation,
  useGetVersionQuery,
  useGetVersionSpliceTableQuery,
  useGetVersionScenarioConfigurationQuery,
  useCreateVersionScenarioConfigurationMutation,
  useUpdateVersionScenarioConfigurationMutation,
  useGetVersionCoreConfigurationQuery,
  useCreateVersionCoreConfigurationMutation,
  useUpdateVersionCoreConfigurationMutation,
  useDeleteVersionCoreConfigurationMutation,
  useLazyGetVersionQuery,
  useGetVersionConfigQuery,
  useLazyGetVersionsQuery,
  useGetVersionsQuery,
  useGetVersionCostBinRangesQuery,
  useLazyGetVersionCostBinRangesQuery,
  useUpdateVersionMutation,
  useDeleteVersionMutation,
  useGetImportStatusQuery,
  useGetVersionStatusQuery,
  useUpdateVersionArchitectureMutation,
  useReapplyDemandModelMutation,
  useGetVersionEstimatedQuotaUsageQuery,
  useGetVersionScreenshotQuery,
} = versionsSlice;

/**
 * Selectors
 */

export const selectByVersionId = (state: Store, { projectId, versionId }: { projectId: string; versionId: string }) => {
  const data = versionsSlice.endpoints.getVersions.select(projectId)(state)?.data;
  return data?.entities[versionId];
};

export const selectVersionsByProjectId = createSelector(
  [
    (state: Store, projectId: string | null) =>
      versionsSlice.endpoints.getVersions.select(projectId ?? skipToken)(state)?.data as EntityState<Version> | undefined,
  ],
  (data) => data?.ids.map((id) => data.entities[id] as Version)
);

export const selectVersionConfig = createDeepEqualSelector(
  [(state: Store) => state, (state: Store, versionId?: string) => versionId],
  (state: Store, versionId) => {
    if (!versionId) return initialConfigState;

    // Call the selector query function directly on the root state
    const queryResult = versionsSlice.endpoints.getVersionConfig.select(versionId)(state);

    // Return data or fallback state cleanly
    return queryResult?.data ?? initialConfigState;
  }
);

/**
 * Returns a flat list of layers and sublayers in the correct order based
 * on their root, group & layer ordering.
 *
 */
export const selectAllVersionLayerConfigsInOrder = createSelector(selectVersionConfig, (configuration) => orderLayersByConfiguration(configuration));

export const { selectAll: selectAllVersionConfigs } = versionConfigEntityAdapter.getSelectors(
  (state: Store, versionId = undefined) => selectVersionConfig(state, versionId ?? state.project.versionId).Data
);

export const selectAllVersionLayers = createSelector(selectVersionConfig, selectLayersFromConfig);

export const selectAllVersionGroupConfigs = createSelector(selectVersionConfig, selectGroupsFromConfig);

export const selectAllVersionStyles = createSelector(selectVersionConfig, selectStylesFromConfig);

/**
 * Returns a flat list of styles that do not have a ancestor Layer that is flagged as excluded.
 * @returns {LayerStyle[]}
 */
export const selectNonExcludedVersionStyles = createSelector(selectVersionConfig, (configuration: Configuration) => {
  const layerConfigs = orderLayersByConfiguration(configuration, { ignoreExcluded: true, ignoreHidden: true });

  return chain(layerConfigs)
    .map("Styles")
    .flatMap()
    .map((styleId) => configuration.Data.entities[styleId] as LayerStyle)
    .value();
});

/**
 * Select a LayerConfig by its Key from the version configuration.
 * This selector is useful when you need the LayerConfig but the Layer resource may not exist yet
 * (e.g., before a layer has been uploaded for the first time).
 * @returns LayerConfig if found, undefined otherwise
 */
export const selectLayerConfigByKey = (state: Store, { versionId, layerKey }: { versionId: string; layerKey: string }) => {
  const config = selectVersionConfig(state, versionId);
  if (!config?.Data?.entities) return undefined;

  return Object.values(config.Data.entities).find((entity): entity is LayerConfig => entity?.Type === "LAYER" && entity.Key === layerKey);
};

/**
 * Generate a dictionary that contains the mapping between LayerKey and Label.
 */
export const selectLayerLabelByLayerKey = createSelector(selectVersionConfig, (config) => {
  let configsList: { [key: string]: string } = {};
  Object.values(config.Data.entities).forEach((item) => {
    if (item && item.Type === "LAYER" && item.Key && item.SubType !== "COST_TO_SERVE") {
      configsList[item.Key] = item.Label;
    }
  });
  return configsList;
});

export const selectVersionMlcId = (state: Store) => {
  const { versionId } = state.project;
  return versionsSlice.endpoints.getVersion.select(versionId)(state)?.data?.MapLayerConfigID || "";
};

export const selectCurrentVersionStatus = (state: Store): ProjectStatus | undefined => {
  const versionId = state.project?.versionId;
  if (!versionId) return undefined;

  const queryResult = versionsSlice.endpoints.getVersionStatus.select(versionId)(state);

  return queryResult?.data;
};

export const refetchVersionStatus = async (dispatch: AppThunkDispatch, versionId: string): Promise<ProjectStatus | undefined> => {
  const result = dispatch(versionsSlice.endpoints.getVersionStatus.initiate(versionId, { forceRefetch: true }));
  const data = (await result)?.data;
  result.unsubscribe();

  return data;
};

export const selectLayerVersionImportStatus = (
  state: Store,
  { versionId, layerId }: { versionId: string; layerId: string }
): ImportStatus | undefined => {
  if (!versionId) return undefined;
  const { data } = versionsSlice.endpoints.getImportStatus.select({ versionId })(state);
  return data?.Layers.find(({ LayerKey }) => LayerKey === layerId)?.ImportStatus.Status;
};
